import * as Sentry from "@sentry/react";
import { sentryLogger } from "./sentryLogger";

export interface NetworkErrorDetails {
  url?: string;
  method?: string;
  status?: number;
  statusText?: string;
  errorMessage?: string;
  component?: string;
  operation?: string;
  metadata?: Record<string, any>;
}

export class NetworkErrorLogger {
  /**
   * Log a network error with both console and Sentry
   */
  static logNetworkError(
    error: Error | unknown,
    details: NetworkErrorDetails
  ): void {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const isNetworkError = this.isNetworkError(error);
    
    // Console logging with detailed context
    const consoleDetails = {
      errorType: isNetworkError ? 'NETWORK_ERROR' : 'API_ERROR',
      message: errorMessage,
      ...details,
      timestamp: new Date().toISOString(),
    };
    
    if (isNetworkError) {
      console.error(`[${details.component || 'NetworkError'}] Network connection failed:`, consoleDetails);
    } else if (details.status && details.status >= 500) {
      console.error(`[${details.component || 'NetworkError'}] Server error:`, consoleDetails);
    } else if (details.status && details.status >= 400) {
      console.warn(`[${details.component || 'NetworkError'}] Client error:`, consoleDetails);
    } else {
      console.error(`[${details.component || 'NetworkError'}] Request failed:`, consoleDetails);
    }
    
    // Sentry logging with categorization
    const sentryContext = {
      component: details.component || 'network',
      operation: details.operation,
      url: details.url,
      httpStatus: details.status,
      httpStatusText: details.statusText,
      errorType: isNetworkError ? 'network' : 'api',
      ...details.metadata,
    };
    
    if (isNetworkError) {
      sentryLogger.error(
        `Network error: ${details.operation || 'Unknown operation'}`,
        error,
        sentryContext
      );
    } else if (details.status && details.status >= 500) {
      sentryLogger.error(
        `Server error (${details.status}): ${details.operation || 'Unknown operation'}`,
        error,
        sentryContext
      );
    } else {
      sentryLogger.warn(
        `Request failed: ${details.operation || 'Unknown operation'}`,
        sentryContext
      );
    }
  }
  
  /**
   * Log a connection state change
   */
  static logConnectionStateChange(
    state: 'connected' | 'disconnected' | 'reconnecting',
    details: {
      component?: string;
      reason?: string;
      metadata?: Record<string, any>;
    }
  ): void {
    const message = `Connection state changed to: ${state}`;
    const context = {
      connectionState: state,
      ...details,
      timestamp: new Date().toISOString(),
    };
    
    // Console logging
    if (state === 'disconnected') {
      console.warn(`[${details.component || 'Connection'}] ${message}`, context);
    } else if (state === 'reconnecting') {
      console.info(`[${details.component || 'Connection'}] ${message}`, context);
    } else {
      console.log(`[${details.component || 'Connection'}] ${message}`, context);
    }
    
    // Sentry logging
    if (state === 'disconnected') {
      sentryLogger.warn(message, context);
    } else {
      sentryLogger.info(message, context);
    }
  }
  
  /**
   * Check if an error is a network error
   */
  static isNetworkError(error: Error | unknown): boolean {
    if (!(error instanceof Error)) return false;
    
    const networkErrorPatterns = [
      'fetch',
      'network',
      'Failed to fetch',
      'NetworkError',
      'ERR_NETWORK',
      'ERR_INTERNET_DISCONNECTED',
      'ERR_CONNECTION',
      'ECONNREFUSED',
      'ETIMEDOUT',
      'ENOTFOUND',
    ];
    
    return networkErrorPatterns.some(pattern => 
      error.message.toLowerCase().includes(pattern.toLowerCase()) ||
      error.name.toLowerCase().includes(pattern.toLowerCase())
    );
  }
  
  /**
   * Log API response time and performance
   */
  static logApiPerformance(
    operation: string,
    startTime: number,
    details: {
      url?: string;
      status?: number;
      component?: string;
      metadata?: Record<string, any>;
    }
  ): void {
    const duration = Date.now() - startTime;
    const isSlowResponse = duration > 3000; // More than 3 seconds
    
    const perfDetails = {
      operation,
      duration: `${duration}ms`,
      ...details,
      timestamp: new Date().toISOString(),
    };
    
    if (isSlowResponse) {
      console.warn(`[${details.component || 'Performance'}] Slow API response:`, perfDetails);
      
      sentryLogger.warn('Slow API response detected', {
        ...perfDetails,
        performanceIssue: true,
      });
    } else if (duration > 1000) {
      console.info(`[${details.component || 'Performance'}] API response time:`, perfDetails);
    }
  }
  
  /**
   * Create a wrapped fetch that includes error logging
   */
  static async fetchWithLogging(
    url: string,
    options: RequestInit & { 
      component?: string; 
      operation?: string;
      metadata?: Record<string, any>;
    } = {}
  ): Promise<Response> {
    const { component, operation, metadata, ...fetchOptions } = options;
    const startTime = Date.now();
    
    try {
      const response = await fetch(url, fetchOptions);
      
      // Log performance
      this.logApiPerformance(operation || url, startTime, {
        url,
        status: response.status,
        component,
        metadata,
      });
      
      // Log errors for non-OK responses
      if (!response.ok) {
        const errorDetails: NetworkErrorDetails = {
          url,
          method: fetchOptions.method || 'GET',
          status: response.status,
          statusText: response.statusText,
          component,
          operation,
          metadata,
        };
        
        this.logNetworkError(
          new Error(`HTTP ${response.status}: ${response.statusText}`),
          errorDetails
        );
      }
      
      return response;
    } catch (error) {
      // Log network errors
      const errorDetails: NetworkErrorDetails = {
        url,
        method: fetchOptions.method || 'GET',
        errorMessage: error instanceof Error ? error.message : String(error),
        component,
        operation,
        metadata,
      };
      
      this.logNetworkError(error, errorDetails);
      throw error;
    }
  }
}

// Export convenience functions
export const logNetworkError = NetworkErrorLogger.logNetworkError.bind(NetworkErrorLogger);
export const logConnectionStateChange = NetworkErrorLogger.logConnectionStateChange.bind(NetworkErrorLogger);
export const fetchWithLogging = NetworkErrorLogger.fetchWithLogging.bind(NetworkErrorLogger);
export const isNetworkError = NetworkErrorLogger.isNetworkError.bind(NetworkErrorLogger);